import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Clock } from "@phosphor-icons/react";
import api from "@/lib/api";

const AuthContext = createContext(null);
const IDLE_MS = 30 * 60 * 1000; // 30 minutes d'inactivité avant l'avertissement
const GRACE_SEC = 60; // délai (secondes) pour prolonger avant déconnexion effective

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = user
  const [checking, setChecking] = useState(true);

  // Revalidation RÉSILIENTE : on ne détruit une session QUE sur un vrai 401
  // (token invalide/expiré/session fermée ailleurs). Les erreurs transitoires
  // (réseau, 5xx, Cloudflare 520/524) ne doivent JAMAIS déconnecter un utilisateur
  // dont le token est valide — on conserve le token et on retente automatiquement.
  const retryRef = useRef(null);
  const loadUser = useCallback(async () => {
    const token = localStorage.getItem("interko_token");
    if (!token) {
      if (retryRef.current) { clearTimeout(retryRef.current); retryRef.current = null; }
      setUser(false);
      setChecking(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      if (retryRef.current) { clearTimeout(retryRef.current); retryRef.current = null; }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        // Session réellement invalide : on nettoie.
        localStorage.removeItem("interko_token");
        setUser(false);
      } else {
        // Erreur transitoire : on GARDE le token. On conserve l'utilisateur déjà
        // chargé s'il existe ; sinon on planifie une nouvelle tentative pour
        // restaurer la session dès que le backend répond (sans re-login).
        setUser((prev) => (prev && typeof prev === "object" ? prev : false));
        if (!retryRef.current) {
          retryRef.current = setTimeout(() => { retryRef.current = null; loadUser(); }, 5000);
        }
      }
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = (token, userObj) => {
    localStorage.setItem("interko_token", token);
    setUser(userObj);
  };

  const logout = useCallback(() => {
    localStorage.removeItem("interko_token");
    setUser(false);
  }, []);

  const refresh = loadUser;

  // Déconnexion par inactivité : avertissement à 30 min avec compte à rebours de 60 s
  // et possibilité de PROLONGER (+30 min). Rien n'est déconnecté en silence.
  const idleTimer = useRef(null);
  const warnActive = useRef(false);
  const [idleWarn, setIdleWarn] = useState(false);

  const triggerWarning = useCallback(() => {
    warnActive.current = true;
    setIdleWarn(true);
  }, []);

  const startIdleTimer = useCallback(() => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(triggerWarning, IDLE_MS);
  }, [triggerWarning]);

  const extendSession = useCallback(() => {
    warnActive.current = false;
    setIdleWarn(false);
    startIdleTimer();
    loadUser(); // revalide la session (détecte une déconnexion sur un autre appareil)
    toast.success("Session prolongée de 30 minutes.");
  }, [startIdleTimer, loadUser]);

  const idleLogout = useCallback((silentToast) => {
    warnActive.current = false;
    setIdleWarn(false);
    logout();
    if (silentToast) toast.info("Session fermée après 30 minutes d'inactivité. Veuillez vous reconnecter.");
  }, [logout]);

  useEffect(() => {
    if (!user) return;
    // L'activité repousse le timer, SAUF pendant l'avertissement (choix explicite requis).
    const reset = () => { if (!warnActive.current) startIdleTimer(); };
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    startIdleTimer();
    return () => {
      events.forEach((e) => window.removeEventListener(e, reset));
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [user, startIdleTimer]);

  return (
    <AuthContext.Provider value={{ user, checking, login, logout, refresh }}>
      {children}
      {idleWarn && (
        <IdleWarningModal
          seconds={GRACE_SEC}
          onExtend={extendSession}
          onExpire={() => idleLogout(true)}
          onLogout={() => idleLogout(false)}
        />
      )}
    </AuthContext.Provider>
  );
}

function IdleWarningModal({ seconds, onExtend, onExpire, onLogout }) {
  const [count, setCount] = useState(seconds);
  useEffect(() => {
    if (count <= 0) return undefined;
    const id = setTimeout(() => setCount((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [count]);
  useEffect(() => {
    if (count === 0) onExpire();
  }, [count, onExpire]);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4" data-testid="idle-warning">
      <div className="bg-surface border border-border rounded-xl p-7 max-w-md w-full text-center shadow-2xl">
        <div className="w-12 h-12 rounded-full bg-accent/10 border border-accent/30 flex items-center justify-center mx-auto mb-4">
          <Clock size={26} weight="duotone" className="text-accent" />
        </div>
        <h3 className="font-display text-xl font-bold text-fg mb-2">Toujours là ?</h3>
        <p className="text-muted text-sm">
          Vous êtes inactif depuis 30 minutes. Par sécurité, votre session va se fermer dans
        </p>
        <p className="text-accent font-mono text-3xl font-bold my-3" data-testid="idle-countdown">{count}s</p>
        <div className="flex flex-col sm:flex-row gap-3 mt-5">
          <button onClick={onExtend} data-testid="extend-session-btn"
            className="flex-1 bg-accent text-black font-semibold py-2.5 rounded-md hover-lift">
            Rester connecté (+30 min)
          </button>
          <button onClick={onLogout} data-testid="idle-logout-btn"
            className="flex-1 border border-border text-fg py-2.5 rounded-md hover:border-accent/40">
            Se déconnecter
          </button>
        </div>
      </div>
    </div>
  );
}

export const useAuth = () => useContext(AuthContext);
